import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import type { SvgIconComponent } from "@mui/icons-material";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import type { NavItem } from "@/components/layout/Sidebar";
import { NavLinkRow } from "@/components/layout/SidebarNavRows";
import { Tooltip } from "@/components/ui/Tooltip";
import { cn } from "@/utils/cn";

const VIEWPORT_MARGIN = 12;
const FLYOUT_GAP = 6;
const FLYOUT_MIN_WIDTH = 208;
const HOVER_OPEN_DELAY_MS = 90;
const HOVER_CLOSE_DELAY_MS = 220;

/**
 * Close callbacks of the currently open rail flyouts. Hovering a sibling closes the
 * previous panel immediately instead of waiting for its leave grace period, so two
 * panels are never visible at once.
 */
const openFlyouts = new Set<() => void>();

function closeOtherFlyouts(self: () => void) {
  for (const closeOther of Array.from(openFlyouts)) {
    if (closeOther !== self) closeOther();
  }
}

function pathMatchesItem(pathname: string, to: string): boolean {
  const base = to.split("?")[0];
  return (
    pathname === base ||
    (base !== "/pos/dashboard" && pathname.startsWith(`${base}/`))
  );
}

type FlyoutCoords = {
  top: number;
  left: number;
  /** Which side of the rail the panel sits on — drives the hover bridge direction. */
  side: "left" | "right";
  maxItemsHeight?: number;
};

type SidebarNavFlyoutProps = {
  label: string;
  icon: SvgIconComponent;
  items: NavItem[];
  hasFeature?: (code: string) => boolean;
};

function clampFlyoutPosition(
  trigger: DOMRect,
  flyout: HTMLElement,
): FlyoutCoords {
  const viewportW = window.innerWidth;
  const viewportH = window.innerHeight;

  let side: FlyoutCoords["side"] = "right";
  let left = trigger.left + trigger.width + FLYOUT_GAP;
  if (left + FLYOUT_MIN_WIDTH > viewportW - VIEWPORT_MARGIN) {
    side = "left";
    left = Math.max(
      VIEWPORT_MARGIN,
      trigger.left - FLYOUT_MIN_WIDTH - FLYOUT_GAP,
    );
  }

  const titleEl = flyout.querySelector(".workspace-nav-flyout-title");
  const itemsEl = flyout.querySelector(
    ".workspace-nav-flyout-items",
  ) as HTMLElement | null;
  const titleHeight = titleEl?.getBoundingClientRect().height ?? 36;
  const flyoutChrome = titleHeight + 16;
  const itemsHeight = itemsEl?.scrollHeight ?? 0;
  const idealHeight = flyoutChrome + itemsHeight;
  const maxPanelHeight = viewportH - VIEWPORT_MARGIN * 2;

  let top = trigger.top;
  let maxItemsHeight: number | undefined;

  if (idealHeight > maxPanelHeight) {
    top = VIEWPORT_MARGIN;
    maxItemsHeight = Math.max(96, maxPanelHeight - flyoutChrome);
  } else if (top + idealHeight > viewportH - VIEWPORT_MARGIN) {
    top = Math.max(VIEWPORT_MARGIN, viewportH - VIEWPORT_MARGIN - idealHeight);
  }

  return { top, left, side, maxItemsHeight };
}

/**
 * Collapsed rail group. A group with sub-items opens its panel on hover and carries no
 * tooltip, because the panel already names the group and lists every heading under it.
 * A single-item group keeps the plain label tooltip.
 */
export function SidebarNavFlyout({
  label,
  icon: Icon,
  items,
  hasFeature,
}: SidebarNavFlyoutProps) {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<FlyoutCoords | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const flyoutRef = useRef<HTMLDivElement>(null);
  const openTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastPointerType = useRef<string>("mouse");
  const hasSubItems = items.length > 1;
  const isActive = items.some((item) =>
    pathMatchesItem(location.pathname, item.to),
  );

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    const flyout = flyoutRef.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    if (flyout) {
      setCoords(clampFlyoutPosition(rect, flyout));
      return;
    }

    setCoords({
      top: Math.max(VIEWPORT_MARGIN, rect.top),
      left: rect.left + rect.width + FLYOUT_GAP,
      side: "right",
    });
  }, []);

  const clearOpenTimer = useCallback(() => {
    if (openTimer.current) {
      clearTimeout(openTimer.current);
      openTimer.current = null;
    }
  }, []);

  const clearCloseTimer = useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);

  const close = useCallback(() => {
    clearOpenTimer();
    clearCloseTimer();
    setOpen(false);
  }, [clearOpenTimer, clearCloseTimer]);

  const openImmediately = useCallback(() => {
    clearOpenTimer();
    clearCloseTimer();
    closeOtherFlyouts(close);
    setOpen(true);
  }, [clearOpenTimer, clearCloseTimer, close]);

  const scheduleOpen = useCallback(() => {
    clearCloseTimer();
    if (open || openTimer.current) return;
    openTimer.current = setTimeout(() => {
      openTimer.current = null;
      closeOtherFlyouts(close);
      setOpen(true);
    }, HOVER_OPEN_DELAY_MS);
  }, [clearCloseTimer, close, open]);

  const scheduleClose = useCallback(() => {
    clearOpenTimer();
    clearCloseTimer();
    closeTimer.current = setTimeout(() => {
      closeTimer.current = null;
      setOpen(false);
    }, HOVER_CLOSE_DELAY_MS);
  }, [clearOpenTimer, clearCloseTimer]);

  const handlePointerEnter = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      lastPointerType.current = event.pointerType || "mouse";
      // Touch has no hover — tapping the trigger toggles the panel instead.
      if (event.pointerType === "touch") return;
      scheduleOpen();
    },
    [scheduleOpen],
  );

  const handlePointerLeave = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      if (event.pointerType === "touch") return;
      scheduleClose();
    },
    [scheduleClose],
  );

  const handleClick = useCallback(
    (event: ReactMouseEvent<HTMLButtonElement>) => {
      const keyboardActivated = event.detail === 0;
      if (keyboardActivated || lastPointerType.current === "touch") {
        if (open) close();
        else openImmediately();
        return;
      }
      // Mouse: hover owns visibility, so a click must not dismiss what hover just opened.
      openImmediately();
    },
    [close, open, openImmediately],
  );

  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();
  }, [open, items.length, updatePosition]);

  useEffect(() => {
    if (!open) return;
    const onReflow = () => updatePosition();
    window.addEventListener("scroll", onReflow, true);
    window.addEventListener("resize", onReflow);
    return () => {
      window.removeEventListener("scroll", onReflow, true);
      window.removeEventListener("resize", onReflow);
    };
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return;
    openFlyouts.add(close);
    return () => {
      openFlyouts.delete(close);
    };
  }, [open, close]);

  useEffect(() => {
    close();
  }, [location.pathname, close]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (flyoutRef.current?.contains(target)) return;
      close();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, close]);

  useEffect(
    () => () => {
      clearOpenTimer();
      clearCloseTimer();
    },
    [clearOpenTimer, clearCloseTimer],
  );

  if (items.length === 0) return null;

  const flyout =
    open && coords
      ? createPortal(
          <div
            ref={flyoutRef}
            className="workspace-nav-flyout workspace-nav-flyout--fixed"
            style={{ top: coords.top, left: coords.left }}
            data-side={coords.side}
            role="menu"
            aria-label={label}
            onPointerEnter={clearCloseTimer}
            onPointerLeave={handlePointerLeave}
          >
            <p className="workspace-nav-flyout-title flex items-center gap-1.5">
              <Icon sx={{ fontSize: 14 }} className="opacity-80" />
              {label}
            </p>
            <div
              className="workspace-nav-flyout-items"
              style={
                coords.maxItemsHeight != null
                  ? { maxHeight: coords.maxItemsHeight }
                  : undefined
              }
            >
              {items.map((item) => (
                <NavLinkRow
                  key={item.to}
                  item={item}
                  collapsed={false}
                  locked={Boolean(
                    item.feature && hasFeature && !hasFeature(item.feature),
                  )}
                  variant="sub"
                  showIcon={false}
                  inFlyout
                />
              ))}
            </div>
          </div>,
          document.body,
        )
      : null;

  return (
    <>
      <Tooltip
        content={hasSubItems ? undefined : label}
        placement="right"
        disabled={open}
        trigger="hover"
      >
        <button
          ref={triggerRef}
          type="button"
          className={cn(
            "workspace-nav-item workspace-nav-item--flyout-trigger w-full",
            hasSubItems && "workspace-nav-item--has-more",
            isActive && "workspace-nav-item--active",
            open && "workspace-nav-item--active",
          )}
          aria-label={label}
          aria-expanded={open}
          aria-haspopup={hasSubItems ? "menu" : undefined}
          onPointerEnter={handlePointerEnter}
          onPointerLeave={handlePointerLeave}
          onClick={handleClick}
        >
          <Icon
            className="workspace-nav-item-icon shrink-0 opacity-90"
            sx={{ fontSize: 20 }}
          />
          {hasSubItems ? (
            <MoreVertIcon
              className="workspace-nav-item-more shrink-0"
              sx={{ fontSize: 14 }}
              aria-hidden
            />
          ) : null}
        </button>
      </Tooltip>
      {flyout}
    </>
  );
}

export function resolveGroupIcon(
  group: { id: string; items: NavItem[] },
  overrides?: Record<string, SvgIconComponent>,
): SvgIconComponent {
  if (overrides?.[group.id]) return overrides[group.id];
  return group.items[0]?.icon ?? FolderOutlinedIcon;
}
